export type Role = 'werewolf' | 'villager' | 'seer' | 'witch' | 'hunter' | 'cupid' | 'white_wolf';

export type GamePhase = 'lobby' | 'rules' | 'night' | 'day' | 'voting' | 'mayor-election' | 'mayor-tiebreaker' | 'elimination' | 'hunter-revenge' | 'end';

export type VoiceType = 
  // Fun & Décalées uniquement
  | 'santa' | 'goblin' | 'grandma' | 'pirate' | 'robot' | 'playful' | 'gollum' | 'joker';

// Tons narratifs Fun & Décalées uniquement
export type NarrativeTone = 'taquin' | 'coquin' | 'quebecois' | 'ivre' | 'sauvage';

// Configuration des règles personnalisables
export interface GameRules {
  enabledRoles: {
    seer: boolean;
    witch: boolean;
    hunter: boolean;
    cupid: boolean;
    white_wolf: boolean;
  };
  werewolfCount: number; // nombre de loups-garous (min 1)
  discussionTime: number; // en secondes (0 = pas de limite)
  votingTime: number; // en secondes (0 = pas de limite)
}

export const DEFAULT_GAME_RULES: GameRules = {
  enabledRoles: {
    seer: true,
    witch: true,
    hunter: true,
    cupid: true,
    white_wolf: false,
  },
  werewolfCount: 1, // calculé dynamiquement par défaut
  discussionTime: 0, // pas de limite par défaut
  votingTime: 0, // pas de limite par défaut
};

export const TIMER_PRESETS = [
  { label: 'Pas de limite', value: 0 },
  { label: '30 secondes', value: 30 },
  { label: '1 minute', value: 60 },
  { label: '2 minutes', value: 120 },
  { label: '3 minutes', value: 180 },
  { label: '5 minutes', value: 300 },
];

export interface NarrativeToneOption {
  id: NarrativeTone;
  name: string;
  description: string;
  icon: string;
  warning?: string;
}

export const NARRATIVE_TONE_OPTIONS: NarrativeToneOption[] = [
  { id: 'taquin', name: 'Taquin', description: 'Humour léger et piques amicales entre potes', icon: '😏' },
  { id: 'coquin', name: 'Coquin', description: 'Humour adulte et grivois, on assume !', icon: '🔥', warning: '18+' },
  { id: 'quebecois', name: 'Québécois', description: 'Tabarnak, c\'est l\'fun en crisse !', icon: '🍁' },
  { id: 'ivre', name: 'Narrateur Bourré', description: 'Le narrateur a abusé sur l\'apéro...', icon: '🍺' },
  { id: 'sauvage', name: 'Mode Sauvage', description: 'Roasts, insultes et trash talk sans filtre', icon: '💀', warning: '+18' },
];

export interface Player {
  id: string;
  name: string;
  role?: Role;
  isAlive: boolean;
  votedFor?: string;
  isLover?: boolean;
  loverId?: string;
}

export interface WitchPotions {
  hasHealPotion: boolean;
  hasKillPotion: boolean;
}

export interface NightEvent {
  turn: number;
  type: 'werewolf_kill' | 'witch_save' | 'witch_kill' | 'seer_reveal' | 'hunter_revenge' | 'lover_death' | 'cupid_lovers' | 'white_wolf_kill';
  actorId?: string;
  targetId?: string;
  secondTargetId?: string;
  targetRole?: Role;
  saved?: boolean;
}

export interface GameStats {
  totalTurns: number;
  werewolfKills: number;
  witchSaves: number;
  witchKills: number;
  seerReveals: number;
  hunterRevenges: number;
  loverDeaths: number;
}

export interface GameState {
  id: string;
  phase: GamePhase;
  players: Player[];
  currentTurn: number;
  nightAction?: {
    werewolfTarget?: string;
    seerTarget?: string;
    witchHealUsed?: boolean;
    witchKillTarget?: string;
    cupidLovers?: [string, string];
    whiteWolfTarget?: string;
  };
  witchPotions: WitchPotions;
  loversChosen: boolean;
  eliminatedTonight?: string[];
  hunterRevengeTarget?: string;
  winner?: 'werewolves' | 'villagers' | 'lovers' | 'white_wolf';
  voiceType: VoiceType;
  narrativeTone: NarrativeTone;
  nightEvents: NightEvent[];
  seerRevealedPlayers: string[]; // IDs of players already revealed by seer
  mayorId?: string; // ID of the current mayor
  pendingMayorElection?: boolean; // True when mayor needs to be elected
  tiebreakerCandidates?: string[]; // Player IDs with equal votes for mayor tiebreaker
}

export type VoiceCategory = 'fun';

export interface VoiceOption {
  id: VoiceType;
  name: string;
  description: string;
  icon: string;
  category: VoiceCategory;
}

export const VOICE_OPTIONS: VoiceOption[] = [
  { id: 'playful', name: 'Québécois', description: 'Voix chaleureuse avec l\'accent du Québec, c\'est l\'fun !', icon: '🍁', category: 'fun' },
  { id: 'santa', name: 'Père Noël', description: 'Ho ho ho ! Voix chaleureuse et bienveillante', icon: '🎅', category: 'fun' },
  { id: 'goblin', name: 'Gobelin', description: 'Voix nasillarde et malicieuse, un peu chelou', icon: '👺', category: 'fun' },
  { id: 'grandma', name: 'Mamie Gâteau', description: 'Voix douce de grand-mère qui raconte des histoires', icon: '👵', category: 'fun' },
  { id: 'pirate', name: 'Pirate', description: 'Arr ! Voix rauque de vieux loup de mer', icon: '🏴‍☠️', category: 'fun' },
  { id: 'robot', name: 'Robot', description: 'BLEEP BLOOP - Voix mécanique du futur', icon: '🤖', category: 'fun' },
  { id: 'gollum', name: 'Gollum', description: 'Mon précieux... Voix sifflante et tourmentée', icon: '💍', category: 'fun' },
  { id: 'joker', name: 'Joker', description: 'Voix dérangée et imprévisible, ça va clasher', icon: '🃏', category: 'fun' },
];

export const ROLE_INFO: Record<Role, { name: string; description: string; team: 'village' | 'werewolf' | 'neutral'; color: string; icon: string }> = {
  werewolf: {
    name: 'Loup-Garou',
    description: 'Chaque nuit, éliminez un villageois avec vos compères loups.',
    team: 'werewolf',
    color: 'wolf',
    icon: '🐺',
  },
  villager: {
    name: 'Villageois',
    description: 'Trouvez et éliminez les loups-garous avant qu\'ils ne vous dévorent tous.',
    team: 'village',
    color: 'villager',
    icon: '🏠',
  },
  seer: {
    name: 'Voyante',
    description: 'Chaque nuit, vous pouvez découvrir le véritable rôle d\'un joueur.',
    team: 'village',
    color: 'seer',
    icon: '🔮',
  },
  witch: {
    name: 'Sorcière',
    description: 'Vous possédez 2 potions : une pour sauver la victime des loups, une pour tuer quelqu\'un.',
    team: 'village',
    color: 'witch',
    icon: '🧪',
  },
  hunter: {
    name: 'Chasseur',
    description: 'Quand vous êtes éliminé, vous pouvez tirer sur un joueur et l\'éliminer avec vous.',
    team: 'village',
    color: 'hunter',
    icon: '🏹',
  },
  cupid: {
    name: 'Cupidon',
    description: 'La première nuit, désignez 2 joueurs qui seront amoureux. Si l\'un meurt, l\'autre aussi.',
    team: 'neutral',
    color: 'cupid',
    icon: '💘',
  },
  white_wolf: {
    name: 'Loup Blanc',
    description: 'Vous êtes un loup solitaire. Une nuit sur deux, après les loups, vous pouvez dévorer un autre loup.',
    team: 'neutral',
    color: 'white_wolf',
    icon: '🐺‍❄️',
  },
};
